import React from 'react';
import { ChevronDown, Copy, Settings } from 'lucide-react';
import { SessionData } from '../../services/embedService';
import MoonPayWidgetButton from './MoonPayWidgetButton';
import { cn } from '../../utils/cn';
import {
  PaymentMethodTab,
  deriveCheckoutAmounts,
  displayNetwork,
  displayTokenSymbol,
  formatPlainAmount,
  openCheckoutUrl,
} from './payCheckoutTypes';

export interface PayCheckoutViewProps {
  session: SessionData;
  methodTab: PaymentMethodTab;
  onMethodTabChange: (tab: PaymentMethodTab) => void;
  terminal: boolean;
  moonPayExtraParams?: Record<string, string>;
  walletAddress: string;
  checkoutUrl: string;
  copied: boolean;
  onCopyWallet: (address: string) => void;
  onRefreshStatus: () => void;
  polling: boolean;
  normalizedStatus: string;
}

const PayCheckoutView: React.FC<PayCheckoutViewProps> = ({
  session,
  methodTab,
  onMethodTabChange,
  terminal,
  moonPayExtraParams,
  walletAddress,
  checkoutUrl,
  copied,
  onCopyWallet,
  onRefreshStatus,
  polling,
  normalizedStatus,
}) => {
  const { fiatCode, fiatAmount, youGet, yourSpend, usdPerToken } = deriveCheckoutAmounts(session);
  const token = displayTokenSymbol(session.cryptoCurrency);
  const network = displayNetwork(session.cryptoCurrency);
  const hasWallet = walletAddress.length > 0;
  const qrUrl = hasWallet
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(walletAddress)}`
    : '';

  return (
    <div className="mx-auto w-full max-w-[548px] px-4 py-6 sm:py-8">
      {/* Outer shell — Figma light blue gradient panel */}
      <div className="overflow-hidden rounded-[16px] border border-[#dbeafe] bg-gradient-to-br from-[#f5faff] via-[#f0f7ff] to-[#e8f2fc] p-4 shadow-[0_4px_24px_rgba(37,99,235,0.08)] sm:p-5">
        {/* Amount ready */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-[#e5e7eb] bg-white px-4 py-3.5 shadow-sm">
          <div>
            <p className="mb-0 text-[11px] font-medium uppercase tracking-[0.12em] text-[#9ca3af]">Amount ready</p>
            <p className="mb-0 text-[20px] font-bold leading-tight text-[#111827]">
              {fiatCode} {formatPlainAmount(fiatAmount)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-[10px] border border-[#d1d5db] bg-[#f3f4f6] px-3.5 py-2 text-[12px] font-medium text-[#374151]"
              onClick={() => window.alert('This payment link uses a fixed amount set when the link was created.')}
            >
              Change amount
            </button>
            {checkoutUrl && !terminal && (
              <button
                type="button"
                className="rounded-[10px] border border-[#93c5fd] bg-white px-3.5 py-2 text-[12px] font-medium text-[#2d9cdb]"
                onClick={() => openCheckoutUrl(checkoutUrl)}
              >
                Open in new tab
              </button>
            )}
          </div>
        </div>

        {/* Pick a method */}
        <div className="mb-3 rounded-[12px] border border-[#e5e7eb] bg-white p-4 shadow-sm">
          <p className="mb-0.5 text-[11px] font-medium uppercase tracking-[0.12em] text-[#9ca3af]">Pick a method</p>
          <p className="mb-3 text-[13px] text-[#6b7280]">Choose the payment path you want to use.</p>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onMethodTabChange('wallet')}
              className={cn(
                'flex w-full items-center justify-between gap-3 rounded-[10px] border px-3.5 py-3 text-left transition',
                methodTab === 'wallet'
                  ? 'border-[#2d9cdb] bg-[#eff6ff]'
                  : 'border-[#e5e7eb] bg-[#f5f5f5]'
              )}
            >
              <div>
                <p className="mb-0 text-[14px] font-bold text-[#111827]">Wallet</p>
                <p className="mb-0 text-[12px] text-[#6b7280]">For {token} already on {network}</p>
              </div>
              <span className="shrink-0 rounded-full bg-[#e8eef4] px-2.5 py-0.5 text-[10px] font-semibold text-[#64748b]">
                Fastest if ready
              </span>
            </button>

            <button
              type="button"
              onClick={() => onMethodTabChange('card')}
              className={cn(
                'flex w-full items-center justify-between gap-3 rounded-[10px] border px-3.5 py-3 text-left transition',
                methodTab === 'card'
                  ? 'border-[#2d9cdb] bg-[#eff6ff]'
                  : 'border-[#e5e7eb] bg-[#f5f5f5]'
              )}
            >
              <div>
                <p className="mb-0 text-[14px] font-bold text-[#111827]">Card/Bank</p>
                <p className="mb-0 text-[12px] text-[#6b7280]">Embedded provider checkout</p>
              </div>
              <span className="shrink-0 rounded-full bg-[#e8eef4] px-2.5 py-0.5 text-[10px] font-semibold text-[#64748b]">
                Recommended
              </span>
            </button>
          </div>

          {methodTab === 'card' && !terminal && (
            <div className="mt-2 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5 text-center">
              <p className="mb-0 text-[12px] text-[#6b7280]">Preparing secure card and bank checkout..</p>
            </div>
          )}

          {methodTab === 'wallet' && !terminal && (
            <div className="mt-2 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5">
              <p className="mb-0 text-[12px] leading-relaxed text-[#6b7280]">
                Send the payment to the wallet address below and keep this page open. We will confirm it here.
              </p>
            </div>
          )}
        </div>

        {/* Card / Bank panel */}
        {!terminal && methodTab === 'card' && (
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-4 shadow-sm">
            <h3 className="mb-2 text-[15px] font-bold text-[#111827]">Pay By Card Or Bank</h3>
            <p className="mb-2 text-[12px] leading-[1.55] text-[#6b7280]">
              Use the secure Onramper widget below to choose the provider and payment method that works best for you.
              When you continue, the selected provider will open in a new tab to complete the payment.
            </p>
            <p className="mb-2 text-[12px] font-medium text-[#6b7280]">Complete Your Secure Card Or Bank Payment Below</p>
            <p className="mb-3 text-[12px] leading-[1.55] text-[#ca8a04]">
              Choose your provider inside the widget. When you continue, the selected provider will open in a new tab
              while this page stays ready for confirmation.
            </p>

            <div className="mb-3 flex flex-wrap gap-2">
              <span className="rounded-full border border-[#ebe6df] bg-[#faf8f5] px-3 py-1 text-[11px] font-medium text-[#57534e]">
                Onramper
              </span>
              <span className="rounded-full border border-[#ebe6df] bg-[#faf8f5] px-3 py-1 text-[11px] font-medium text-[#57534e]">
                Open provider tab on click
              </span>
            </div>

            {moonPayExtraParams && (
              <div className="mb-2 grid grid-cols-2 gap-2">
                <MoonPayWidgetButton
                  flow="buy"
                  label="Pay"
                  className="w-full rounded-[12px] border border-[#d1d5db] bg-white py-3 text-[13px] font-semibold text-[#111827] hover:bg-[#fafafa]"
                  extraParams={moonPayExtraParams}
                  onSuccess={onRefreshStatus}
                  onClose={onRefreshStatus}
                />
                <MoonPayWidgetButton
                  flow="buy"
                  label="Buy"
                  className="w-full rounded-[12px] border border-[#d1d5db] bg-white py-3 text-[13px] font-semibold text-[#111827] hover:bg-[#fafafa]"
                  extraParams={moonPayExtraParams}
                  onSuccess={onRefreshStatus}
                  onClose={onRefreshStatus}
                />
              </div>
            )}

            {checkoutUrl && (
              <button
                type="button"
                className="mb-4 w-full rounded-[12px] border border-[#d1d5db] bg-white py-3.5 text-[13px] font-semibold text-[#111827] hover:bg-[#fafafa]"
                onClick={() => openCheckoutUrl(checkoutUrl)}
              >
                Open Widget In New Tab
              </button>
            )}

            {/* Widget preview — matches Figma nested grey panel */}
            <div className="rounded-[12px] border border-[#e5e7eb] bg-[#f3f4f6] p-3">
              <div className="mb-2 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="mb-0 text-[11px] text-[#9ca3af]">You spend</p>
                    <p className="mb-0 text-[18px] font-bold text-[#111827]">{formatPlainAmount(yourSpend)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 rounded-full border border-[#e5e7eb] bg-white px-2.5 py-1">
                    <span className="text-sm leading-none">🇺🇸</span>
                    <span className="text-[12px] font-semibold text-[#111827]">{fiatCode}</span>
                  </div>
                </div>
              </div>

              <div className="mb-0 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="mb-0 text-[11px] text-[#9ca3af]">You get</p>
                    <p className="mb-0 text-[18px] font-bold text-[#111827]">{formatPlainAmount(youGet)}</p>
                  </div>
                  <div className="text-right">
                    <div className="inline-flex items-center gap-1 rounded-full border border-[#e5e7eb] bg-white px-2.5 py-1">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2775ca] text-[9px] font-bold text-white">
                        $
                      </span>
                      <span className="text-[12px] font-semibold text-[#111827]">{token}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-end gap-1">
                      <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-[#8247e5] text-[8px] font-bold text-white">
                        ⬡
                      </span>
                      <span className="text-[11px] font-semibold text-[#8247e5]">{network}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-[8px] bg-[#ececec] px-3 py-2">
                <p className="mb-0 text-[11px] text-[#6b7280]">
                  1 {token} IS = {formatPlainAmount(usdPerToken)} {fiatCode}
                </p>
                {moonPayExtraParams && (
                  <div className="inline-flex items-center gap-0.5">
                    <span className="text-[11px] text-[#6b7280]">By</span>
                    <MoonPayWidgetButton
                      flow="buy"
                      label="MoonPay"
                      className="!inline-flex items-center gap-0.5 rounded-none border-0 bg-transparent p-0 text-[11px] font-semibold text-[#111827] shadow-none hover:bg-transparent"
                      extraParams={moonPayExtraParams}
                      onSuccess={onRefreshStatus}
                      onClose={onRefreshStatus}
                    />
                    <ChevronDown size={12} className="text-[#9ca3af]" aria-hidden />
                  </div>
                )}
              </div>

              <p className="mb-2 mt-3 text-[11px] text-[#9ca3af]">By Using</p>
              <div className="mb-3 flex items-center justify-between rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#1a3b5d] text-[10px] font-bold text-white">
                    R
                  </span>
                  <div>
                    <p className="mb-0 text-[12px] font-semibold text-[#111827]">Revolut Pay</p>
                    <p className="mb-0 text-[10px] font-semibold text-[#10b981]">Recommended</p>
                  </div>
                </div>
                <ChevronDown size={16} className="text-[#9ca3af]" />
              </div>

              <div className="border-t border-[#e0e0e0] pt-3 text-center">
                <p className="mb-2 inline-flex items-center justify-center gap-1 text-[11px] text-[#9ca3af]">
                  <Settings size={12} strokeWidth={1.75} /> Help &amp; Settings
                </p>
                <p className="mb-0 text-[11px] text-[#9ca3af]">
                  Powered By <span className="font-bold text-[#111827]">onramper</span>
                </p>
              </div>
            </div>

            <p className="mb-1 mt-3 text-[11px] leading-[1.5] text-[#9ca3af]">
              If you don&apos;t see any Provider available for your payment , try choosing payment method in the
              &quot;Pay using&quot; drop down.
            </p>
            <p className="mb-0 text-[11px] leading-[1.5] text-[#9ca3af]">
              If your provider opens a new tab, keep this page open and come back here after you finish.
            </p>
          </div>
        )}

        {/* Wallet panel */}
        {!terminal && methodTab === 'wallet' && (
          <div className="rounded-[12px] border border-[#e5e7eb] bg-white p-4 shadow-sm">
            <h3 className="mb-1 text-[15px] font-bold text-[#111827]">Pay By Wallet</h3>
            <p className="mb-2 text-[12px] text-[#6b7280]">
              Send {token} on {network} to the escrow deposit address below, then keep this page open while we confirm it.
            </p>
            <div className="mb-4 rounded-[10px] border border-[#fde68a] bg-[#fffbeb] px-3 py-2.5">
              <p className="mb-0 text-[12px] leading-relaxed text-[#92400e]">
                <strong>Manual test:</strong> Copy the deposit address into MetaMask and send USDC (Polygon).
                Card checkout is optional for this phase — deposit completes the split cycle.
              </p>
            </div>

            <div className="mb-4 rounded-[12px] border border-[#bae7ff] bg-[#e6f4ff] px-4 py-4 text-center">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#6b7280]">Pay this amount</p>
              <p className="mb-2 text-[26px] font-bold leading-none text-[#111827]">
                {formatPlainAmount(fiatAmount)} {token}
              </p>
              <div className="flex justify-center gap-2">
                <span className="rounded-full border border-[#e5e7eb] bg-white px-3 py-0.5 text-[11px] font-medium text-[#374151]">
                  {token}
                </span>
                <span className="rounded-full border border-[#e5e7eb] bg-white px-3 py-0.5 text-[11px] font-medium text-[#374151]">
                  {network}
                </span>
              </div>
            </div>

            <div className="mb-4 space-y-2">
              {[
                'Copy the escrow deposit address or scan the code',
                `Send ${token} on ${network} from MetaMask`,
                'Wait here, then settle/claim in the admin app',
              ].map((text, index) => (
                <div
                  key={text}
                  className="flex items-center gap-3 rounded-[10px] border border-[#e5e7eb] bg-white px-3 py-2.5"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#dbeafe] text-[12px] font-bold text-[#2d9cdb]">
                    {index + 1}
                  </span>
                  <p className="mb-0 text-[12px] text-[#111827]">{text}</p>
                </div>
              ))}
            </div>

            <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#9ca3af]">Payment details</p>
            <p className="mb-1 text-[14px] font-bold text-[#111827]">Escrow deposit address</p>
            <p className="mb-4 text-[12px] leading-[1.5] text-[#6b7280]">
              This is the smart-contract address for this payment only. Send USDC here to fund the Com + Dest split.
            </p>

            {hasWallet ? (
              <>
                <p className="mb-3 text-center text-[14px] font-bold text-[#111827]">Scan To Pay</p>
                <img
                  src={qrUrl}
                  alt="Scan to pay"
                  className="mx-auto mb-4 block"
                  width={240}
                  height={240}
                />
                <div className="mb-4 rounded-[10px] bg-[#f5f5f5] px-3 py-3">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#9ca3af]">Deposit address</p>
                  <p className="mb-0 break-all text-[14px] font-bold leading-snug text-[#111827]">{walletAddress}</p>
                </div>
                <button
                  type="button"
                  className="mb-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#2d9cdb] py-3.5 text-[13px] font-bold text-white"
                  onClick={() => onCopyWallet(walletAddress)}
                >
                  <Copy size={16} strokeWidth={2} />
                  {copied ? 'Copied' : 'Copy deposit address'}
                </button>
                <a
                  href={`https://polygonscan.com/address/${walletAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center rounded-full border border-[#e5e7eb] bg-white py-3.5 text-[13px] font-bold text-[#111827] hover:bg-[#fafafa]"
                >
                  View In Explorer
                </a>
              </>
            ) : (
              <p className="text-[12px] text-amber-700">Wallet address is not available for this session yet.</p>
            )}

            <div className="mt-4 rounded-[10px] bg-[#f5f5f5] px-3 py-3">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#9ca3af]">
                Current amount window
              </p>
              <button
                type="button"
                className="mb-1 border-0 bg-transparent p-0 text-[12px] font-medium text-[#2d9cdb]"
                onClick={() => void onRefreshStatus()}
              >
                Updating amount
              </button>
              <p className="mb-0 text-[11px] leading-[1.45] text-[#9ca3af]">
                Small Timing Or Amount Differences Are Still Reviewed Before Settlement.
              </p>
            </div>
          </div>
        )}

        {/* Status strip */}
        <div className="mt-3 flex items-center justify-between rounded-[10px] border border-[#e5e7eb]/80 bg-white/70 px-3 py-2">
          <p className="mb-0 text-[10px] text-[#9ca3af]">
            {polling && !terminal ? 'Checking status every 10s…' : `Status: ${normalizedStatus}`}
          </p>
          <button
            type="button"
            className="border-0 bg-transparent p-0 text-[10px] font-medium text-[#6b7280] underline-offset-2 hover:underline"
            onClick={() => void onRefreshStatus()}
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayCheckoutView;
